import React from 'react';
import { Box, Stack, Typography } from '@mui/material';

/**
 * Professional navy / slate report theme (Option A).
 * Body: Source Sans 3 · Headings: Lora
 */
const tones = {
  page: '#F8F9FA',
  card: '#FFFFFF',
  ink: '#1E1E1E',
  /** Darker body for remarks / help copy */
  inkStrong: '#0B1220',
  heading: '#1A365D',
  muted: '#4A5568',
  accent: '#3AAFA9',
  accentSoft: '#E6F7F6',
  slateSoft: '#EDF2F7',
  border: '#E2E8F0',
  up: '#2F855A',
  down: '#C05621',
  stable: '#4A5568',
};

const fonts = {
  body: '"Source Sans 3", "Segoe UI", "Helvetica Neue", Arial, sans-serif',
  heading: 'Lora, Georgia, "Times New Roman", serif',
};

function firstName(fullName) {
  const part = String(fullName || '')
    .trim()
    .split(/\s+/)
    .filter(Boolean)[0];
  return part || 'your child';
}

function SectionCard({ title, children, eyebrow, lead }) {
  return (
    <Box
      sx={{
        bgcolor: tones.card,
        borderRadius: 2.5,
        border: `1px solid ${tones.border}`,
        p: { xs: 2.5, md: 3.25 },
        mb: 2.5,
        boxShadow: '0 1px 2px rgba(26, 54, 93, 0.04)',
      }}
    >
      {eyebrow ? (
        <Typography
          variant="overline"
          sx={{
            color: tones.accent,
            letterSpacing: 1.5,
            fontWeight: 700,
            fontFamily: fonts.body,
            fontSize: '0.7rem',
          }}
        >
          {eyebrow}
        </Typography>
      ) : null}
      <Typography
        variant="h5"
        sx={{
          color: tones.heading,
          fontWeight: 600,
          fontFamily: fonts.heading,
          mb: lead ? 0.75 : 2,
          mt: eyebrow ? 0.35 : 0,
          fontSize: { xs: '1.35rem', md: '1.45rem' },
          letterSpacing: '-0.01em',
        }}
      >
        {title}
      </Typography>
      {lead ? (
        <Typography
          variant="body2"
          sx={{
            color: tones.muted,
            mb: 2,
            maxWidth: 560,
            fontFamily: fonts.body,
            fontSize: '0.95rem',
            lineHeight: 1.55,
          }}
        >
          {lead}
        </Typography>
      ) : null}
      {children}
    </Box>
  );
}

function StatTile({ label, value }) {
  return (
    <Box
      sx={{
        flex: '1 1 120px',
        minWidth: 110,
        bgcolor: tones.accentSoft,
        borderRadius: 2,
        border: `1px solid ${tones.border}`,
        px: 1.75,
        py: 1.5,
      }}
    >
      <Typography
        variant="caption"
        sx={{ color: tones.muted, display: 'block', mb: 0.25, fontFamily: fonts.body, fontWeight: 600 }}
      >
        {label}
      </Typography>
      <Typography
        variant="h5"
        sx={{ color: tones.accent, fontWeight: 700, lineHeight: 1.1, fontFamily: fonts.body }}
      >
        {value}
      </Typography>
    </Box>
  );
}

function uniqueHelpTexts(parameters = []) {
  const seen = new Set();
  const texts = [];
  for (const p of parameters || []) {
    const t = String(p.helpText || '').trim();
    if (!t) continue;
    const key = t.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    texts.push(t);
  }
  return texts;
}

function moduleParentComments(mod) {
  const remarks = String(mod?.moduleRemarks || mod?.remarks || '').trim();
  const helpTexts = uniqueHelpTexts(mod?.parameters);
  return { remarks, helpTexts, hasContent: Boolean(remarks || helpTexts.length) };
}

function SkillCard({ module: mod }) {
  const { remarks, helpTexts, hasContent } = moduleParentComments(mod);
  if (!hasContent) return null;

  return (
    <Box
      sx={{
        borderRadius: 2.5,
        border: `1px solid ${tones.border}`,
        bgcolor: tones.card,
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          px: { xs: 1.75, md: 2.25 },
          pt: 1.75,
          pb: 1.25,
          bgcolor: tones.slateSoft,
          borderBottom: `1px solid ${tones.border}`,
        }}
      >
        <Typography
          variant="caption"
          sx={{
            color: tones.muted,
            fontWeight: 700,
            letterSpacing: 1.1,
            textTransform: 'uppercase',
            fontFamily: fonts.body,
          }}
        >
          Skill
        </Typography>
        <Typography
          sx={{
            color: tones.heading,
            fontWeight: 600,
            fontSize: '1.1rem',
            mt: 0.15,
            fontFamily: fonts.heading,
          }}
        >
          {mod.moduleName}
        </Typography>
      </Box>

      <Box sx={{ px: { xs: 1.75, md: 2.25 }, py: 1.75 }}>
        {helpTexts.length > 0 ? (
          <Stack spacing={1} sx={{ mb: remarks ? 1.5 : 0 }}>
            {helpTexts.map((text) => (
              <Typography
                key={text.slice(0, 48)}
                variant="body1"
                sx={{
                  color: tones.inkStrong,
                  lineHeight: 1.7,
                  fontFamily: fonts.body,
                  fontSize: { xs: '1.05rem', md: '1.08rem' },
                  fontWeight: 500,
                }}
              >
                {text}
              </Typography>
            ))}
          </Stack>
        ) : null}
        {remarks ? (
          <Typography
            variant="body1"
            sx={{
              color: tones.inkStrong,
              whiteSpace: 'pre-wrap',
              lineHeight: 1.7,
              fontFamily: fonts.body,
              fontSize: { xs: '1.05rem', md: '1.08rem' },
              fontWeight: 500,
              fontStyle: helpTexts.length ? 'italic' : 'normal',
            }}
          >
            {remarks}
          </Typography>
        ) : null}
      </Box>
    </Box>
  );
}

function isInternalDemoReadyModule(mod) {
  const code = String(mod?.moduleCode || '').toLowerCase();
  if (code === 'demo_ready') return true;
  const name = String(mod?.moduleName || '').toLowerCase().trim();
  return name === 'demo ready' || name === 'demo-ready' || name === 'demo ready skill';
}

function AssessmentBody({ assessment, childFirst }) {
  if (!assessment) return null;
  const modules = (assessment.modules || [])
    .filter((mod) => !isInternalDemoReadyModule(mod))
    .filter((mod) => moduleParentComments(mod).hasContent);
  return (
    <SectionCard
      title={assessment.title || `How ${childFirst} is growing`}
      eyebrow="Growth"
      lead="A short note for each skill — what it means for your child this month."
    >
      {modules.length === 0 ? (
        <Typography sx={{ color: tones.muted, fontFamily: fonts.body }}>
          No teacher notes for this month.
        </Typography>
      ) : (
        <Stack spacing={2}>
          {modules.map((mod) => (
            <SkillCard key={mod.moduleId || mod.moduleName} module={mod} />
          ))}
        </Stack>
      )}
      {assessment.overallRemarks ? (
        <Box
          sx={{
            mt: 2.25,
            bgcolor: tones.slateSoft,
            borderRadius: 2,
            border: `1px solid ${tones.border}`,
            px: 2,
            py: 1.75,
          }}
        >
          <Typography
            variant="subtitle2"
            sx={{ color: tones.heading, mb: 0.5, fontWeight: 700, fontFamily: fonts.body }}
          >
            Overall remarks
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: tones.inkStrong,
              whiteSpace: 'pre-wrap',
              lineHeight: 1.7,
              fontFamily: fonts.body,
              fontSize: { xs: '1.05rem', md: '1.08rem' },
              fontWeight: 500,
            }}
          >
            {assessment.overallRemarks}
          </Typography>
        </Box>
      ) : null}
    </SectionCard>
  );
}

function topicActivityCount(topic) {
  const acts = topic?.activities || [];
  if (acts.length) return acts.length;
  return 0;
}

function TeachingBody({ teaching, legacyModules, childFirst }) {
  const batches = teaching?.batches?.length
    ? teaching.batches
    : legacyModules?.length
      ? [
          {
            batchName: 'Class',
            homeworkSentCount: teaching?.homeworkSentCount,
            topics: legacyModules.map((m) => ({
              label: m.label,
              sessionCount: m.sessionCount,
              type: m.type,
              activities: m.activities || [],
            })),
          },
        ]
      : [];

  const rows = batches.flatMap((batch) =>
    (batch.topics || []).map((topic) => ({
      batchName: batch.batchName,
      moduleName: topic.label,
      activityCount: topicActivityCount(topic),
      key: `${batch.batchName}-${topic.type || topic.label}`,
    }))
  );

  return (
    <SectionCard
      title={teaching?.title || `What ${childFirst} practiced in class`}
      eyebrow="In class"
      lead="Modules covered in class this month, with how many activities were practiced in each."
    >
      {(teaching?.homeworkSentCount ?? 0) > 0 ? (
        <Stack direction="row" spacing={1.25} useFlexGap flexWrap="wrap" sx={{ mb: 2.25 }}>
          <StatTile label="Homework sent" value={teaching?.homeworkSentCount ?? 0} />
        </Stack>
      ) : null}

      {rows.length === 0 ? (
        <Typography sx={{ color: tones.muted, fontFamily: fonts.body }}>
          No class activity recorded for this month.
        </Typography>
      ) : (
        <Box
          sx={{
            border: `1px solid ${tones.border}`,
            borderRadius: 2,
            overflow: 'hidden',
            bgcolor: tones.card,
          }}
        >
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: '1fr 140px',
              gap: 1,
              px: 1.75,
              py: 1.25,
              bgcolor: tones.slateSoft,
              borderBottom: `1px solid ${tones.border}`,
            }}
          >
            <Typography
              variant="caption"
              sx={{ color: tones.muted, fontWeight: 700, letterSpacing: 0.4, fontFamily: fonts.body }}
            >
              Module
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: tones.muted,
                fontWeight: 700,
                letterSpacing: 0.4,
                textAlign: 'right',
                fontFamily: fonts.body,
              }}
            >
              Activities practiced
            </Typography>
          </Box>
          {rows.map((row, idx) => (
            <Box
              key={row.key}
              sx={{
                display: 'grid',
                gridTemplateColumns: '1fr 140px',
                gap: 1,
                px: 1.75,
                py: 1.35,
                borderBottom: idx < rows.length - 1 ? `1px solid ${tones.border}` : 'none',
                alignItems: 'center',
              }}
            >
              <Box>
                <Typography sx={{ color: tones.ink, fontWeight: 600, fontFamily: fonts.body }}>
                  {row.moduleName}
                </Typography>
                {batches.length > 1 ? (
                  <Typography variant="caption" sx={{ color: tones.muted, fontFamily: fonts.body }}>
                    {row.batchName}
                  </Typography>
                ) : null}
              </Box>
              <Typography
                sx={{ color: tones.accent, fontWeight: 700, textAlign: 'right', fontFamily: fonts.body }}
              >
                {row.activityCount}
              </Typography>
            </Box>
          ))}
        </Box>
      )}
    </SectionCard>
  );
}

function FromTeacherBody({ fromTeacher }) {
  if (!fromTeacher?.narrative) return null;
  const { studentProgress, nextMonthFocus } = fromTeacher.narrative;
  if (!String(studentProgress || '').trim() && !String(nextMonthFocus || '').trim()) return null;

  return (
    <SectionCard
      title={fromTeacher.title || 'A note from the teacher'}
      eyebrow="Personal note"
      lead="A short message written for your family."
    >
      {studentProgress ? (
        <Typography
          variant="body1"
          sx={{
            color: tones.ink,
            whiteSpace: 'pre-wrap',
            mb: nextMonthFocus ? 1.75 : 0,
            lineHeight: 1.7,
            fontFamily: fonts.body,
            fontSize: '1.02rem',
          }}
        >
          {studentProgress}
        </Typography>
      ) : null}
      {nextMonthFocus ? (
        <Box
          sx={{
            bgcolor: tones.accentSoft,
            borderRadius: 2,
            p: 1.75,
            border: `1px solid ${tones.border}`,
          }}
        >
          <Typography
            variant="caption"
            sx={{ color: tones.heading, fontWeight: 700, letterSpacing: 0.4, fontFamily: fonts.body }}
          >
            Looking ahead
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: tones.ink, whiteSpace: 'pre-wrap', mt: 0.5, lineHeight: 1.65, fontFamily: fonts.body }}
          >
            {nextMonthFocus}
          </Typography>
        </Box>
      ) : null}
    </SectionCard>
  );
}

/** Legacy v1 layout for already-sent snapshots that cannot be enriched. */
function LegacyV1Body({ snap }) {
  return (
    <Box>
      <SectionCard title={snap.sectionA?.title || 'What we taught this month'}>
        <Typography sx={{ fontFamily: fonts.body, color: tones.ink }}>
          Sessions attended: {snap.sectionA?.sessionsAttended}
        </Typography>
        <Typography sx={{ mb: 2, fontFamily: fonts.body, color: tones.ink }}>
          Homework sent: {snap.sectionA?.homeworkSentCount} sessions
        </Typography>
        {(snap.sectionA?.modules || []).map((m) => (
          <Box key={m.type} sx={{ mb: 1 }}>
            <Typography variant="body2" fontWeight={600} sx={{ fontFamily: fonts.body, color: tones.ink }}>
              • {m.label} ({m.sessionCount} sessions)
            </Typography>
            {(m.activities || []).map((act) => (
              <Typography
                key={act.name}
                variant="caption"
                sx={{ display: 'block', pl: 2, color: tones.muted, fontFamily: fonts.body }}
              >
                – {act.name}
              </Typography>
            ))}
          </Box>
        ))}
      </SectionCard>
      {(snap.sectionB?.modules || []).some((m) => moduleParentComments(m).hasContent) ? (
        <SectionCard title={snap.sectionB?.title || "Your child's assessment"}>
          {(snap.sectionB?.modules || [])
            .filter((m) => moduleParentComments(m).hasContent)
            .map((m) => {
              const { remarks, helpTexts } = moduleParentComments(m);
              return (
                <Box key={m.moduleId} sx={{ mb: 1.5 }}>
                  <Typography fontWeight={600} sx={{ fontFamily: fonts.heading, color: tones.heading }}>
                    {m.moduleName}
                  </Typography>
                  {helpTexts.map((text) => (
                    <Typography
                      key={text.slice(0, 40)}
                      variant="body1"
                      sx={{
                        fontFamily: fonts.body,
                        color: tones.inkStrong,
                        mt: 0.5,
                        lineHeight: 1.7,
                        fontSize: '1.05rem',
                        fontWeight: 500,
                      }}
                    >
                      {text}
                    </Typography>
                  ))}
                  {remarks ? (
                    <Typography
                      variant="body1"
                      sx={{
                        fontFamily: fonts.body,
                        color: tones.inkStrong,
                        whiteSpace: 'pre-wrap',
                        mt: 0.5,
                        lineHeight: 1.7,
                        fontSize: '1.05rem',
                        fontWeight: 500,
                        fontStyle: helpTexts.length ? 'italic' : 'normal',
                      }}
                    >
                      {remarks}
                    </Typography>
                  ) : null}
                </Box>
              );
            })}
        </SectionCard>
      ) : null}
    </Box>
  );
}

/**
 * Shared parent-facing snapshot renderer.
 * v2+ = assessment first + teaching (with activities) + optional from-teacher.
 * v1 = legacy layout for already-sent reports.
 */
export default function ParentReportSnapshotView({ snapshot, compact = false }) {
  if (!snapshot) return null;
  const isV2 = Number(snapshot.version) >= 2;
  const childFirst = firstName(snapshot.student?.name);

  return (
    <Box
      sx={{
        bgcolor: compact ? 'transparent' : tones.page,
        borderRadius: compact ? 0 : 3,
        p: compact ? 0 : { xs: 1.25, md: 2 },
        fontFamily: fonts.body,
        color: tones.ink,
      }}
    >
      <Box
        sx={{
          textAlign: 'left',
          mb: 2.75,
          py: compact ? 1.25 : 2.5,
          px: { xs: 2, md: 2.75 },
          borderRadius: 2.5,
          bgcolor: compact ? 'transparent' : tones.card,
          border: compact ? 'none' : `1px solid ${tones.border}`,
          borderLeft: compact ? 'none' : `4px solid ${tones.heading}`,
          boxShadow: compact ? 'none' : '0 1px 2px rgba(26, 54, 93, 0.04)',
          position: 'relative',
        }}
      >
        <Typography
          variant="overline"
          sx={{
            color: tones.accent,
            letterSpacing: 2.2,
            fontWeight: 700,
            fontFamily: fonts.body,
            fontSize: '0.7rem',
          }}
        >
          Bacchapanti
        </Typography>
        <Typography
          variant="h4"
          sx={{
            color: tones.heading,
            fontWeight: 600,
            mt: 0.5,
            fontFamily: fonts.heading,
            fontSize: { xs: '1.55rem', md: '1.85rem' },
            lineHeight: 1.25,
            letterSpacing: '-0.015em',
          }}
        >
          Monthly progress for {childFirst}
        </Typography>
        <Typography
          sx={{
            color: tones.muted,
            mt: 1,
            maxWidth: 520,
            fontFamily: fonts.body,
            fontSize: '0.975rem',
            lineHeight: 1.55,
          }}
        >
          A clear picture of how {childFirst} is learning — growth, practice in class, and a note from the
          teacher.
        </Typography>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={{ xs: 0.35, sm: 1.5 }}
          sx={{ mt: 1.75 }}
          flexWrap="wrap"
          useFlexGap
        >
          <Typography variant="body2" sx={{ color: tones.ink, fontWeight: 700, fontFamily: fonts.body }}>
            {snapshot.student?.name}
          </Typography>
          <Typography variant="body2" sx={{ color: tones.muted, fontFamily: fonts.body }}>
            {snapshot.monthLabel}
            {snapshot.student?.displayId ? ` · ID ${snapshot.student.displayId}` : ''}
            {snapshot.teacherName ? ` · Teacher ${snapshot.teacherName}` : ''}
          </Typography>
        </Stack>
      </Box>

      {isV2 ? (
        <>
          <AssessmentBody
            assessment={snapshot.assessment || snapshot.sectionB}
            childFirst={childFirst}
          />
          <TeachingBody
            teaching={snapshot.teaching || snapshot.sectionA}
            legacyModules={snapshot.sectionA?.modules}
            childFirst={childFirst}
          />
          <FromTeacherBody fromTeacher={snapshot.fromTeacher || snapshot.sectionD} />
        </>
      ) : (
        <LegacyV1Body snap={snapshot} />
      )}
    </Box>
  );
}
